import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

const EventDetailView = ({ event }) => {
  const [copiedItems, setCopiedItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');

  if (!event) {
    return <div>No event data available</div>;
  }

  const formatValue = (value, isCustomProperty = false) => {
    if (value === null || value === undefined) return null;
    
    // For custom properties, preserve all values including empty strings, 0, false
    if (isCustomProperty) {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          return JSON.stringify(value, null, 2);
        }
        return JSON.stringify(value, null, 2);
      }
      return String(value); // Return as-is, even if empty string
    }
    
    // For standard properties, filter out empty strings
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? JSON.stringify(value, null, 2) : null;
      }
      return Object.keys(value).length > 0 ? JSON.stringify(value, null, 2) : null;
    }
    const stringValue = String(value).trim();
    return stringValue.length > 0 ? stringValue : null;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, label]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(label);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Helper to get element data (from element_data object or individual fields)
  const getElementData = () => {
    if (event.element_data && typeof event.element_data === 'object') {
      return event.element_data;
    }
    // Fallback to individual fields
    const elementData = {};
    if (event.element_name) elementData.element_name = event.element_name;
    if (event.element_id) elementData.element_id = event.element_id;
    if (event.element_class) elementData.element_class = event.element_class;
    if (event.element_tag_name) elementData.element_tag_name = event.element_tag_name;
    if (event.element_type) elementData.element_type = event.element_type;
    if (event.element_text) elementData.element_text = event.element_text;
    if (event.element_position) elementData.element_position = event.element_position;
    return Object.keys(elementData).length > 0 ? elementData : null;
  };

  const elementData = getElementData();

  /** Parse JSONB / API fields that may arrive as object or JSON string */
  const parseJsonObject = (raw) => {
    if (raw == null) return null;
    if (typeof raw === 'string') {
      try {
        const p = JSON.parse(raw);
        return p && typeof p === 'object' && !Array.isArray(p) ? p : null;
      } catch {
        return null;
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return Object.keys(raw).length > 0 ? raw : null;
    }
    return null;
  };

  const groupIdsRaw = event.group_membership_ids;
  const groupMembershipDisplay =
    groupIdsRaw == null
      ? null
      : Array.isArray(groupIdsRaw)
        ? groupIdsRaw.length
          ? JSON.stringify(groupIdsRaw)
          : null
        : typeof groupIdsRaw === 'string' && groupIdsRaw.trim()
          ? groupIdsRaw
          : null;

  // Build all standard properties list (excluding custom_properties / auto_event_data — those go in payload tab)
  const allProperties = [
    // Event Information
    { label: 'Event ID', value: event.id != null ? String(event.id) : null, copyable: true },
    { label: 'Event Name', value: event.event_name },
    { label: 'Event Type', value: event.event_type },
    { label: 'Event Category', value: event.event_category },
    { label: 'Timestamp', value: event.timestamp ? new Date(event.timestamp).toLocaleString() : null },
    
    // Session & User
    { label: 'Session ID', value: event.session_uuid || event.session_id, copyable: true },
    { label: 'User ID', value: event.user_id, copyable: true },
    { label: 'Distinct ID', value: event.distinct_id, copyable: true },
    { label: 'User identity ID', value: event.user_identity_id != null ? String(event.user_identity_id) : null, copyable: true },
    { label: 'Group membership IDs', value: groupMembershipDisplay, copyable: true },
    
    // Location
    { label: 'Country', value: event.country },
    { label: 'City', value: event.city },
    { label: 'Region', value: event.region },
    { label: 'Latitude', value: event.latitude },
    { label: 'Longitude', value: event.longitude },
    { label: 'Timezone', value: event.timezone },
    
    // Device & Browser
    { label: 'Browser', value: event.browser_name },
    { label: 'Browser Version', value: event.browser_version },
    { label: 'OS', value: event.os },
    { label: 'OS Version', value: event.os_version },
    { label: 'Device Type', value: event.device_type },
    { label: 'Language', value: event.language },
    { label: 'Screen Resolution', value: event.screen_width && event.screen_height ? `${event.screen_width}x${event.screen_height}` : null },
    { label: 'Viewport', value: event.viewport_width && event.viewport_height ? `${event.viewport_width}x${event.viewport_height}` : null },
    { label: 'DPI', value: event.dpi },
    
    // Page Information
    { label: 'Page Title', value: event.page_title },
    { label: 'Current URL', value: event.current_url, copyable: true },
    { label: 'URL', value: event.uri, copyable: true },
    { label: 'Initial Referrer', value: event.initial_referrer, copyable: true },
    { label: 'Referrer', value: event.referrer, copyable: true },
    
    // UTM Parameters
    { label: 'UTM Source', value: event.utm_source },
    { label: 'UTM Medium', value: event.utm_medium },
    { label: 'UTM Campaign', value: event.utm_campaign },
    { label: 'UTM Term', value: event.utm_term },
    { label: 'UTM Content', value: event.utm_content },
    { label: 'UTM ID', value: event.utm_id },
    
    // Web3 Information
    { label: 'Is Web3 User', value: event.is_web3_user !== undefined ? (event.is_web3_user ? 'Yes' : 'No') : null },
    { label: 'Wallet Address', value: event.wallet_address, copyable: true },
    
    // Element Information
    { label: 'Element Name', value: elementData?.element_name },
    { label: 'Element ID', value: elementData?.element_id },
    { label: 'Element Class', value: elementData?.element_class },
    { label: 'Element Tag', value: elementData?.element_tag_name },
    { label: 'Element Type', value: elementData?.element_type },
    { label: 'Element Text', value: elementData?.element_text },
    { label: 'Element Position', value: elementData?.element_position ? JSON.stringify(elementData.element_position, null, 2) : null },
  ];

  // custom_properties: any event type (track() can attach props; schema stores JSONB)
  const customPropsObj = parseJsonObject(event.custom_properties);

  const customPropertyRows =
    customPropsObj && Object.keys(customPropsObj).length > 0
      ? Object.keys(customPropsObj).map((key) => {
          const rawValue = customPropsObj[key];
          const formattedValue = formatValue(rawValue, true);
          return {
            label: key,
            value: formattedValue,
            rawValue,
            copyable: true,
          };
        })
      : [];

  // auto_event_data: SDK auto-captured payloads (scroll, click coords, etc.)
  const autoPropsObj = parseJsonObject(event.auto_event_data);
  const autoEventPropertyRows =
    autoPropsObj && Object.keys(autoPropsObj).length > 0
      ? Object.keys(autoPropsObj).map((key) => {
          const rawValue = autoPropsObj[key];
          const formattedValue = formatValue(rawValue, true);
          return {
            label: `Auto · ${key}`,
            value: formattedValue,
            rawValue,
            copyable: true,
          };
        })
      : [];

  const isMeaningfulPayloadRow = (prop) => {
    const rawValue = prop.rawValue;
    if (rawValue === null || rawValue === undefined) return false;
    if (typeof rawValue === 'string' && rawValue.trim() === '') return false;
    if (Array.isArray(rawValue) && rawValue.length === 0) return false;
    if (typeof rawValue === 'object' && !Array.isArray(rawValue) && Object.keys(rawValue).length === 0)
      return false;
    return true;
  };

  const meaningfulCustomOnly = customPropertyRows.filter(isMeaningfulPayloadRow);

  const meaningfulAutoRows = autoEventPropertyRows.filter(isMeaningfulPayloadRow);

  // Filter out properties with null/empty values
  const meaningfulAllProperties = allProperties
    .map(prop => ({ ...prop, value: formatValue(prop.value) }))
    .filter(prop => prop.value !== null && prop.value !== undefined && prop.value !== '');

  // "All properties": fixed/standard fields + custom_properties + auto_event_data
  const meaningfulAllCombined = [
    ...meaningfulAllProperties,
    ...meaningfulCustomOnly,
    ...meaningfulAutoRows,
  ];

  // Same design as TransactionPropertiesTab: grey divide, hover, 3 per row
  const renderPropertiesTable = (properties, emptyMessage = 'No properties found.') => {
    if (properties.length === 0) {
      return (
        <div className="px-6 py-8 text-center text-[#475569]">
          <p className="text-sm">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-100">
        {Array.from({ length: Math.ceil(properties.length / 3) }).map((_, rowIndex) => {
          const rowProps = properties.slice(rowIndex * 3, rowIndex * 3 + 3);
          return (
            <div
              key={`row-${event.id}-${rowIndex}`}
              className="px-6 py-2 hover:bg-gray-50 transition-colors grid grid-cols-3 gap-4 items-center"
            >
              {rowProps.map((prop, colIndex) => {
                const valueToCopy =
                  prop.rawValue !== undefined
                    ? typeof prop.rawValue === 'object'
                      ? JSON.stringify(prop.rawValue, null, 2)
                      : String(prop.rawValue)
                    : prop.value != null
                      ? String(prop.value)
                      : '';
                const canCopy = valueToCopy.length > 0;
                const titleText =
                  valueToCopy.length > 80 ? `${valueToCopy.slice(0, 2000)}${valueToCopy.length > 2000 ? '…' : ''}` : valueToCopy;

                return (
                  <div
                    key={`prop-${event.id}-${rowIndex}-${colIndex}`}
                    className="group/prop flex items-center gap-2 min-w-0 w-full"
                  >
                    <span
                      className="text-xs font-medium text-[#0f172a] shrink-0 max-w-[34%] truncate"
                      style={{ fontFamily: 'Raleway, sans-serif' }}
                      title={prop.label}
                    >
                      {prop.label}
                    </span>
                    <span
                      className="text-sm text-[#475569] flex-1 min-w-0 truncate"
                      style={{ fontFamily: 'Raleway, sans-serif' }}
                      title={titleText || undefined}
                    >
                      {prop.value}
                    </span>
                    {canCopy && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(valueToCopy, prop.label)}
                        className="shrink-0 p-1 rounded text-[#475569] opacity-0 pointer-events-none transition-opacity duration-150 group-hover/prop:opacity-100 group-hover/prop:pointer-events-auto hover:bg-gray-200"
                        title="Copy full value"
                        aria-label={`Copy ${prop.label}`}
                      >
                        {copiedItems.has(prop.label) ? (
                          <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={2} />
                        ) : (
                          <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
              {rowProps.length < 3 && Array.from({ length: 3 - rowProps.length }).map((_, emptyIndex) => (
                <div key={`empty-${emptyIndex}`} />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const tabButtons = [
    { id: 'all', label: 'All properties' },
    { id: 'custom', label: 'Custom' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50/50 px-6 py-3">
        <nav className="flex gap-2">
          {tabButtons.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-200 text-[#0f172a]'
                  : 'bg-gray-100 text-[#475569] hover:bg-gray-200 hover:text-[#475569]'
              }`}
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-0">
        {activeTab === 'all' ? (
          <div>{renderPropertiesTable(meaningfulAllCombined)}</div>
        ) : (
          <div>
            {renderPropertiesTable(
              meaningfulCustomOnly,
              'No custom properties on this event.'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailView;
